/**
 * Client for communicating with the Remotion Player iframe.
 * Handles postMessage protocol, PING/READY handshake, and iframe load fallback.
 *
 * Usage:
 *   const preview = new RemotionPreview(iframeEl, {
 *     onFrameUpdate: (frame) => { ... },
 *     onReady: () => { ... },
 *   })
 *   preview.sendComposition(composition)
 *   preview.sendCompositionAndWait(composition)  // waits for player render
 *   preview.seekTo(frame)
 *   preview.play({ muted })
 *   preview.playRange(from, to, { muted })  // joue la plage et s'arrête seul, de retour sur from
 *   preview.pause()
 *   preview.destroy()
 */
export class RemotionPreview {
  constructor(iframeEl, { onFrameUpdate, onReady, onRangeEnd } = {}) {
    this._iframe = iframeEl
    this._ready = false
    this._onFrameUpdate = onFrameUpdate
    this._onReady = onReady
    this._onRangeEnd = onRangeEnd

    this._messageHandler = (event) => {
      if (event.data?.type === "PREVIEW_READY") {
        this._ready = true
        this._onReady?.()
      }
      if (event.data?.type === "FRAME_UPDATE") {
        this._onFrameUpdate?.(event.data.frame)
      }
      if (event.data?.type === "RANGE_ENDED") {
        this._onRangeEnd?.()
      }
      if (event.data?.type === "COMPOSITION_RENDERED") {
        this._ackResolve?.()
        this._ackResolve = null
      }
    }
    window.addEventListener("message", this._messageHandler)

    // Fallback: when iframe fires load, ping it
    this._iframeLoadHandler = () => {
      this._post({ type: "PING" })
      setTimeout(() => {
        if (!this._ready) {
          this._ready = true
          this._onReady?.()
        }
      }, 200)
    }
    this._iframe.addEventListener("load", this._iframeLoadHandler)
  }

  sendComposition(composition) {
    if (!this._ready) return
    this._post({ type: "UPDATE_COMPOSITION", composition })
  }

  sendCompositionAndWait(composition, timeout = 500) {
    if (!this._ready) return Promise.resolve()
    return new Promise((resolve) => {
      clearTimeout(this._ackTimer)
      this._ackResolve = resolve
      this._ackTimer = setTimeout(() => {
        this._ackResolve = null
        resolve()
      }, timeout)
      this._post({ type: "UPDATE_COMPOSITION", composition, ack: true })
    })
  }

  seekTo(frame) {
    this._post({ type: "SEEK_TO_FRAME", frame })
  }

  play({ muted } = {}) {
    this._post({ type: "PLAY", muted })
  }

  /** La lecture d'une plage (images) : le lecteur s'arrête de lui-même à `to`, revient sur
   *  `from` et prévient (onRangeEnd). Tout autre ordre l'interrompt. */
  playRange(from, to, { muted = false } = {}) {
    this._post({ type: "PLAY_RANGE", from, to, muted })
  }

  pause() {
    this._post({ type: "PAUSE" })
  }

  get ready() {
    return this._ready
  }

  destroy() {
    window.removeEventListener("message", this._messageHandler)
    this._iframe?.removeEventListener("load", this._iframeLoadHandler)
    clearTimeout(this._ackTimer)
  }

  _post(data) {
    this._iframe?.contentWindow?.postMessage(data, "*")
  }
}
